import {
  PreviewIssue,
  PreviewResult,
  PreviewWacImpact,
} from "./bulk_stock_upload_repo";

const inr = (n: number | null | undefined) =>
  n == null
    ? "-"
    : `₹${n.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const rateSourceLabel: Record<string, string> = {
  item_wac: "Item WAC",
  source_item_wac: "Source item WAC",
  derived_po_price: "Avg PO price",
  none: "Not valued",
};

// Value impact of approving: what the WAC ledger will record, priced with the
// same rates the backend committer will write.
const WacImpact = ({ impact }: { impact: PreviewWacImpact }) => (
  <div id="wac_impact_section" className="border rounded-md bg-white p-3 space-y-2">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="text-xs font-semibold text-gray-700">
        WAC / value impact on approval
      </p>
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <span className="text-green-700">In {inr(impact.inward_value)}</span>
        <span className="text-red-600">Out {inr(impact.outward_value)}</span>
        <span className="font-semibold">
          Net {impact.net_value > 0 ? "+" : impact.net_value < 0 ? "−" : ""}
          {inr(Math.abs(impact.net_value))}
        </span>
      </div>
    </div>

    {impact.unvalued_qty > 0 && (
      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
        {impact.unvalued_qty} unit(s) will move without any valuation — no WAC
        history and no PO price to value them at.
      </p>
    )}

    <div className="max-h-64 overflow-auto">
      <table className="min-w-full text-xs divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Item Code</th>
            <th className="px-3 py-2 text-left font-medium">Direction</th>
            <th className="px-3 py-2 text-right font-medium">Qty</th>
            <th className="px-3 py-2 text-right font-medium">Rate</th>
            <th className="px-3 py-2 text-left font-medium">Rate Source</th>
            <th className="px-3 py-2 text-right font-medium">Value</th>
            <th className="px-3 py-2 text-right font-medium">WAC After</th>
            <th className="px-3 py-2 text-left font-medium">Note</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {impact.lines.map((l, i) => (
            <tr id={`wac_row_${l.item_code}_${i}`} key={i} className={l.value == null ? "bg-amber-50" : ""}>
              <td className="px-3 py-1.5">{l.item_code}</td>
              <td
                className={`px-3 py-1.5 font-medium ${
                  l.action === "Inward" ? "text-green-700" : "text-red-600"
                }`}
              >
                {l.action}
              </td>
              <td className="px-3 py-1.5 text-right">{l.qty}</td>
              <td className="px-3 py-1.5 text-right">{inr(l.rate)}</td>
              <td className="px-3 py-1.5">
                {rateSourceLabel[l.rate_source] ?? l.rate_source}
              </td>
              <td className="px-3 py-1.5 text-right">{inr(l.value)}</td>
              <td className="px-3 py-1.5 text-right">
                {l.projected_wac_value != null
                  ? `${inr(l.current_wac_value)} → ${inr(
                      l.projected_wac_value,
                    )}`
                  : inr(l.current_wac_value)}
              </td>
              <td className="px-3 py-1.5 whitespace-normal">{l.note ?? "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const Stat = ({ label, value }: { label: string; value: number }) => (
  <div id={`stat_${label.toLowerCase().replace(/\s+/g, "_")}`} className="border rounded-md bg-white py-2">
    <div className="text-base font-semibold">{value}</div>
    <div className="text-[11px] text-gray-500">{label}</div>
  </div>
);

const IssueList = ({
  title,
  issues,
  tone,
}: {
  title: string;
  issues: PreviewIssue[];
  tone: "red" | "amber";
}) => {
  const toneCls =
    tone === "red"
      ? "border-red-200 bg-red-50 text-red-700"
      : "border-amber-200 bg-amber-50 text-amber-700";
  return (
    <div id={`issue_list_${tone}`} className={`border rounded-md p-3 text-xs space-y-1 ${toneCls}`}>
      <p className="font-semibold">{title}</p>
      <ul className="list-disc pl-4 space-y-1">
        {issues.map((iss, i) => (
          <li id={`issue_row_${tone}_${i}`} key={i}>
            {iss.message}
            {iss.items && iss.items.length > 0 && (
              <span className="opacity-80">
                {" "}
                (
                {iss.items
                  .slice(0, 20)
                  .map((it) =>
                    typeof it === "string"
                      ? it
                      : (it.unique_id ?? it.item_code),
                  )
                  .join(", ")}
                {iss.items.length > 20
                  ? `, +${iss.items.length - 20} more`
                  : ""}
                )
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

const outcomeCls: Record<string, string> = {
  will_apply: "text-green-700",
  blocked: "text-red-600",
  skipped: "text-gray-500",
  warning: "text-amber-600",
};

export default function StockActionPreviewPanel({
  preview,
}: {
  preview: PreviewResult;
}): JSX.Element {
  const c = preview.counts;
  return (
    <div id="preview_panel" className="border rounded-md p-4 space-y-3 bg-gray-50">
      <div className="flex items-center gap-2">
        <span
          className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
            preview.ok
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {preview.ok ? "Ready to raise" : "Blocked"}
        </span>
        <p className="text-sm font-medium text-gray-700">{preview.summary}</p>
      </div>

      <div className="grid grid-cols-4 gap-2 text-center">
        <Stat label="Will apply" value={c.will_apply} />
        <Stat label="Blocked" value={c.blocked} />
        <Stat label="Skipped" value={c.skipped} />
        <Stat label="Warnings" value={c.warnings} />
      </div>

      {preview.blocking_errors.length > 0 && (
        <IssueList
          title="Blocking issues — resolve these before raising"
          issues={preview.blocking_errors}
          tone="red"
        />
      )}
      {preview.warnings.length > 0 && (
        <IssueList title="Warnings" issues={preview.warnings} tone="amber" />
      )}

      {preview.wac_impact && preview.wac_impact.lines.length > 0 && (
        <WacImpact impact={preview.wac_impact} />
      )}

      {preview.rows && preview.rows.length > 0 && (
        <div className="max-h-64 overflow-auto border rounded-md bg-white">
          <table className="min-w-full text-xs divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-medium">#</th>
                <th className="px-3 py-2 text-left font-medium">Key</th>
                <th className="px-3 py-2 text-left font-medium">Outcome</th>
                <th className="px-3 py-2 text-left font-medium">Movement</th>
                <th className="px-3 py-2 text-right font-medium">Qty</th>
                <th className="px-3 py-2 text-left font-medium">Note</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {preview.rows.map((r, i) => (
                <tr id={`preview_row_${r.key}`} key={i}>
                  <td className="px-3 py-1.5">{i + 1}</td>
                  <td className="px-3 py-1.5">{r.key}</td>
                  <td
                    className={`px-3 py-1.5 font-medium ${
                      outcomeCls[r.outcome] ?? ""
                    }`}
                  >
                    {r.outcome.replace("_", " ")}
                  </td>
                  <td className="px-3 py-1.5">{r.movement_type ?? "-"}</td>
                  <td className="px-3 py-1.5 text-right">{r.qty ?? "-"}</td>
                  <td className="px-3 py-1.5">{r.message ?? "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
